// Season view — the operator's home. Slim header + Stages / Season Teams / Schedule.
// Stages tab shows the whole Stage → Group → Gameday hierarchy inline.

import React, { useState, useMemo, useEffect, useRef } from 'react'
import { CircularProgress } from '@material-ui/core'
import TOKENS, { avatarColor } from './tokens'
import {
  Icon, Badge, Card, Avatar, Button, IconBtn, SectionTitle, SelectInput, Empty, ProgressBar, ExpandCollapseControl, DragHandle, SeasonSyncInfo, useHoverCard,
} from './Primitives'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { fmtDateShort, fmtDateRange, fmtTime, periodTone, toneLabel, prettyGroupType, seasonTone, countPlayed, isFinal, gamedayBadge, applyDrag } from './helpers'
import { isLiteStatsMode } from '../../../utils/constants/statsModeOptions'

const PAGE = { padding: 24, maxWidth: 1240, margin: '0 auto' }

/* =============================================================== Season ==== */

const SeasonView = ({
  competition,
  season,
  seasons = [],
  stages = [],
  seasonTeams = [],
  isLoadingStages,
  isLoadingTeams,
  tab,
  setTab,
  nav,
  actions,
}) => {
  const tone = seasonTone(season)
  const otherSeasons = seasons.length > 1

  return (
    <div style={PAGE}>
      {/* Slim header */}
      <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: '14px 20px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, fontWeight: 500, color: TOKENS.text3, marginBottom: 4 }}>
          <span>{competition?.name}</span>
          <Icon name="chevronRight" size={12} />
          <span>Season</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            {season.logoUrl && <img src={season.logoUrl} alt={season.name} style={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0 }} />}
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text, whiteSpace: 'nowrap', lineHeight: 1 }}>{season.name}</h1>
            {tone === 'live' ? <Badge tone="live" dot>Live</Badge> : tone === 'upcoming' ? <Badge tone="upcoming" dot>Upcoming</Badge> : tone === 'past' ? <Badge tone="past">Archived</Badge> : null}
            {isLiteStatsMode(season.statsMode) && <Badge tone="coral">Lite</Badge>}
            <span style={{ fontSize: 12, color: TOKENS.text3, fontFamily: TOKENS.mono, whiteSpace: 'nowrap' }}>
              {fmtDateShort(season.startDate)} → {fmtDateShort(season.endDate)}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {otherSeasons && (
              <div style={{ minWidth: 170 }}>
                <SelectInput
                  value={season.id}
                  onChange={(e) => actions.selectSeason(seasons.find((s) => s.id === e.target.value))}
                  options={seasons.map((s) => ({ value: s.id, label: s.name + (seasonTone(s) === 'live' ? ' · Live' : '') }))}
                />
              </div>
            )}
            {actions.refreshSeasonSync && season.externalCmsSync && !season.externalCmsSync.isPaused && (
              <Button
                variant="outline"
                size="sm"
                icon="sync"
                disabled={actions.refreshSeasonSyncLoading}
                onClick={() => actions.refreshSeasonSync(season)}
              >
                {actions.refreshSeasonSyncLoading ? 'Syncing…' : 'Sync'}
              </Button>
            )}
            <SeasonSyncInfo sync={season.externalCmsSync} seasonName={season.name} />
            {(actions.editSeason || actions.deleteSeason) && <div style={{ width: 1, height: 24, background: TOKENS.border, margin: '0 4px' }} />}
            {actions.editSeason && <IconBtn icon="edit" tone="coral" title="Edit season" onClick={() => actions.editSeason(season)} />}
            {actions.deleteSeason && <IconBtn icon="trash" tone="danger" title="Delete season" onClick={() => actions.deleteSeason(season)} />}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, borderBottom: `1px solid ${TOKENS.border}` }}>
        {[
          { id: 'stages', label: 'Stages', count: stages.length },
          { id: 'teams', label: 'Season Teams', count: seasonTeams.length },
          { id: 'schedule', label: 'Schedule' },
        ].map((t) => {
          const active = t.id === tab
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                position: 'relative', padding: '0 14px', height: 40, fontSize: 13, fontWeight: 500,
                fontFamily: TOKENS.font, background: 'transparent', border: 'none',
                borderBottom: active ? `2px solid ${TOKENS.coral}` : '2px solid transparent', marginBottom: -1,
                cursor: 'pointer', color: active ? TOKENS.text : TOKENS.text3, display: 'inline-flex',
                alignItems: 'center', gap: 8,
              }}
            >
              {t.label}
              {t.count !== undefined && (
                <span style={{ fontSize: 11, padding: '0 6px', height: 18, display: 'inline-flex', alignItems: 'center', borderRadius: 999, background: active ? TOKENS.coralSoft : TOKENS.surface2, color: active ? TOKENS.coralDark : TOKENS.text3 }}>
                  {t.count}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div style={{ paddingTop: 20 }}>
        {tab === 'stages' && <StagesPanel stages={stages} isLoading={isLoadingStages} nav={nav} actions={actions} />}
        {tab === 'teams' && <SeasonTeamsPanel teams={seasonTeams} isLoading={isLoadingTeams} actions={actions} assignButton={actions.assignTeamSeasonButton} />}
        {tab === 'schedule' && <SchedulePanel season={season} stages={stages} nav={nav} actions={actions} />}
      </div>
    </div>
  )
}

/* ============================================================== Stages ===== */

const StagesPanel = ({ stages, isLoading, nav, actions }) => {
  const hover = useHoverCard()
  const [collapsed, setCollapsed] = useState({})
  const toggle = (id) => setCollapsed((c) => ({ ...c, [id]: !c[id] }))
  const expandAll = () => setCollapsed({})
  const collapseAll = () => setCollapsed(Object.fromEntries(stages.map((s) => [s.id, true])))
  const allExpanded = stages.every((s) => !collapsed[s.id])

  // Default expansion per stage set: if some (but not all) stages are live, expand only the
  // live one(s) and collapse the rest; otherwise expand all. Runs once per distinct stage set
  // (so it re-applies when switching season) and never clobbers later manual toggles.
  const stageSig = stages.map((s) => s.id).join(',')
  const initRef = useRef(null)
  useEffect(() => {
    if (initRef.current === stageSig) return
    initRef.current = stageSig
    const live = stages.filter((s) => periodTone(s.startDate, s.endDate) === 'live')
    if (live.length > 0 && live.length < stages.length) {
      setCollapsed(Object.fromEntries(stages.filter((s) => periodTone(s.startDate, s.endDate) !== 'live').map((s) => [s.id, true])))
    } else {
      setCollapsed({})
    }
  }, [stageSig, stages])

  if (isLoading) {
    return <div style={{ textAlign: 'center', padding: 48 }}><CircularProgress style={{ color: TOKENS.coral }} /></div>
  }

  const onDragEnd = (result) => {
    const { source, destination, type } = result
    if (!destination || source.droppableId !== destination.droppableId || source.index === destination.index) return
    if (type === 'STAGE') {
      applyDrag(stages, source.index, destination.index, actions.reorderStages)
    } else if (type.startsWith('GROUP:')) {
      const stageId = type.slice(6)
      const stage = stages.find((s) => s.id === stageId)
      applyDrag(stage?.groups || [], source.index, destination.index, (ids) => actions.reorderGroups(stageId, ids))
    } else if (type.startsWith('GAMEDAY:')) {
      const groupId = type.slice(8)
      let list = []
      stages.forEach((s) => (s.groups || []).forEach((g) => { if (g.id === groupId) list = g.gamedays || [] }))
      applyDrag(list, source.index, destination.index, (ids) => actions.reorderGamedays(groupId, ids))
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {stages.length > 1 && <ExpandCollapseControl allExpanded={allExpanded} onExpandAll={expandAll} onCollapseAll={collapseAll} />}
          <div style={{ fontSize: 12, color: TOKENS.text3 }}>{stages.length} stage{stages.length === 1 ? '' : 's'}</div>
        </div>
        {actions.createStage && <Button variant="primary" icon="plus" onClick={actions.createStage}>Add Stage</Button>}
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="stages" type="STAGE">
          {(stagesDrop) => (
            <div ref={stagesDrop.innerRef} {...stagesDrop.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {stages.map((stage, si) => {
                const isOpen = !collapsed[stage.id]
                const tone = periodTone(stage.startDate, stage.endDate)
                const groups = stage.groups || []
                return (
                  <Draggable key={stage.id} draggableId={stage.id} index={si} isDragDisabled={!actions.reorderStages}>
                    {(stageDrag) => (
                      <div ref={stageDrag.innerRef} {...stageDrag.draggableProps} style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, overflow: 'hidden', ...stageDrag.draggableProps.style }}>
                        {/* Stage header */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '14px 16px', borderBottom: isOpen ? `1px solid ${TOKENS.border}` : 'none' }}>
                          {actions.reorderStages && <DragHandle handleProps={stageDrag.dragHandleProps} />}
                          <IconBtn icon={isOpen ? 'chevronDown' : 'chevronRight'} onClick={() => toggle(stage.id)} title={isOpen ? 'Collapse' : 'Expand'} />
                          <div style={{ width: 36, height: 36, borderRadius: TOKENS.radiusSm, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: tone === 'live' ? TOKENS.liveBg : tone === 'upcoming' ? TOKENS.upcomingBg : tone === 'past' ? TOKENS.pastBg : TOKENS.surface2, color: tone === 'live' ? TOKENS.liveText : tone === 'upcoming' ? TOKENS.upcomingText : tone === 'past' ? TOKENS.pastText : TOKENS.text3 }}>
                            <Icon name="layers" size={16} />
                          </div>
                          <button type="button" onClick={() => nav.openStage(stage)} className={hover.nameHover} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'inherit' }}>{stage.name}</h3>
                              {tone !== 'neutral' && <Badge tone={tone} dot>{toneLabel(tone)}</Badge>}
                            </div>
                            <div style={{ fontSize: 11, color: TOKENS.text3, fontFamily: TOKENS.mono, marginTop: 2 }}>{fmtDateRange(stage.startDate, stage.endDate)}</div>
                          </button>
                          <div style={{ textAlign: 'right', marginRight: 12 }}>
                            <div style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', color: TOKENS.text3 }}>Groups</div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: TOKENS.text, lineHeight: 1, marginTop: 2 }}>{groups.length}</div>
                          </div>
                          {actions.editStage && <IconBtn icon="edit" tone="coral" title="Edit stage" onClick={() => actions.editStage(stage)} />}
                          {actions.deleteStage && <IconBtn icon="trash" tone="danger" title="Delete stage" onClick={() => actions.deleteStage(stage)} />}
                        </div>

                        {/* Groups */}
                        {isOpen && (
                          <div style={{ background: TOKENS.surface, padding: 16 }}>
                            {groups.length === 0 ? (
                              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                                <div style={{ fontSize: 12, color: TOKENS.text3, marginBottom: actions.createGroup ? 8 : 0 }}>No groups in this stage yet</div>
                                {actions.createGroup && <Button variant="outline" size="sm" icon="plus" onClick={() => actions.createGroup(stage)}>Add group</Button>}
                              </div>
                            ) : (
                              <Droppable droppableId={`groups:${stage.id}`} type={`GROUP:${stage.id}`}>
                                {(groupsDrop) => (
                                  <div ref={groupsDrop.innerRef} {...groupsDrop.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    {groups.map((g, gi) => (
                                      <Draggable key={g.id} draggableId={g.id} index={gi} isDragDisabled={!actions.reorderGroups}>
                                        {(groupDrag) => (
                                          <div ref={groupDrag.innerRef} {...groupDrag.draggableProps} style={groupDrag.draggableProps.style}>
                                            <GroupBlock stage={stage} group={g} nav={nav} actions={actions} hover={hover} dragHandleProps={actions.reorderGroups ? groupDrag.dragHandleProps : undefined} />
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {groupsDrop.placeholder}
                                    {actions.createGroup && (
                                      <button type="button" onClick={() => actions.createGroup(stage)} style={dashedBtn}>
                                        <Icon name="plus" size={12} /> Add group
                                      </button>
                                    )}
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                )
              })}
              {stagesDrop.placeholder}

              {actions.createStage && stages.length > 0 && (
                <button type="button" onClick={actions.createStage} style={{ ...dashedBtn, height: 48, fontSize: 13 }}>
                  <Icon name="plus" size={14} /> Add Stage
                </button>
              )}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

const GroupBlock = ({ stage, group, nav, actions, hover, dragHandleProps }) => {
  const gamedays = group.gamedays || []
  return (
    <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radiusSm, overflow: 'hidden' }}>
      {/* Group header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: `1px solid ${TOKENS.border}` }}>
        {dragHandleProps && <DragHandle handleProps={dragHandleProps} />}
        <div style={{ width: 28, height: 28, borderRadius: TOKENS.radiusSm, background: TOKENS.coralSoft, color: TOKENS.coralDark, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon name="grid" size={13} />
        </div>
        <button type="button" onClick={() => nav.openGroup(stage, group)} className={hover.nameHover} style={{ flex: 1, minWidth: 0, textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, color: 'inherit' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <h4 style={{ margin: 0, fontSize: 13, fontWeight: 600, color: 'inherit' }}>{group.name}</h4>
            <Badge>{prettyGroupType(group.type)}</Badge>
            {Array.isArray(group.teams) && <span style={{ fontSize: 11, color: TOKENS.text3 }}>· {group.teams.length} teams</span>}
          </div>
        </button>
        {actions.editGroup && <IconBtn icon="edit" tone="coral" title="Edit group" onClick={() => actions.editGroup(group)} />}
        {actions.deleteGroup && <IconBtn icon="trash" tone="danger" title="Delete group" onClick={() => actions.deleteGroup(group)} />}
      </div>

      {/* Gamedays */}
      <div style={{ padding: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '0.05em', color: TOKENS.text3, textTransform: 'uppercase', marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Gamedays · {gamedays.length}</span>
          {actions.createGamedayBulk && (
            <button type="button" onClick={() => actions.createGamedayBulk(group)} style={{ color: TOKENS.coral, fontSize: 11, fontWeight: 500, border: 'none', background: 'transparent', cursor: 'pointer', textTransform: 'none', letterSpacing: 'normal', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Icon name="plus" size={11} /> Add gamedays
            </button>
          )}
        </div>
        {gamedays.length === 0 ? (
          <div style={{ fontSize: 11, color: TOKENS.text3, fontStyle: 'italic', padding: '8px 4px' }}>No gamedays yet.</div>
        ) : (
          <Droppable droppableId={`gamedays:${group.id}`} type={`GAMEDAY:${group.id}`}>
            {(gdDrop) => (
              <div ref={gdDrop.innerRef} {...gdDrop.droppableProps} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {gamedays.map((gd, i) => {
                  const t = periodTone(gd.startDate, gd.endDate)
                  const gdGames = gd.games || []
                  const played = countPlayed(gdGames)
                  return (
                    <Draggable key={gd.id} draggableId={gd.id} index={i} isDragDisabled={!actions.reorderGamedays}>
                      {(gdDrag) => (
                        <div
                          ref={gdDrag.innerRef}
                          {...gdDrag.draggableProps}
                          role="button"
                          tabIndex={0}
                          onClick={() => nav.openGameday(stage, group, gd)}
                          className={hover.card}
                          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: TOKENS.surface, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radiusSm, textAlign: 'left', cursor: 'pointer', ...gdDrag.draggableProps.style }}
                        >
                          {actions.reorderGamedays && <DragHandle handleProps={gdDrag.dragHandleProps} />}
                          <div style={{ width: 28, height: 28, borderRadius: TOKENS.radiusSm, background: TOKENS.surface2, color: TOKENS.text2, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                            {gamedayBadge(gd, i)}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.text }}>{gd.name}</div>
                              {t === 'neutral' ? (
                                <span style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.05em', color: TOKENS.text3, fontWeight: 600 }}>Unscheduled</span>
                              ) : (
                                <span style={{ width: 6, height: 6, borderRadius: 999, background: t === 'live' ? TOKENS.liveDot : t === 'upcoming' ? TOKENS.upcomingDot : TOKENS.pastDot }} />
                              )}
                            </div>
                            <div style={{ fontSize: 10, color: TOKENS.text3, fontFamily: TOKENS.mono, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fmtDateRange(gd.startDate, gd.endDate)}</div>
                          </div>
                          <div style={{ textAlign: 'right', flexShrink: 0 }}>
                            <div style={{ fontSize: 10.5, fontFamily: TOKENS.mono, color: TOKENS.text2 }}>{played}/{gdGames.length}</div>
                            <div style={{ width: 48, marginTop: 3 }}><ProgressBar value={played} total={gdGames.length || 1} /></div>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  )
                })}
                {gdDrop.placeholder}
              </div>
            )}
          </Droppable>
        )}
      </div>
    </div>
  )
}

const dashedBtn = {
  width: '100%',
  padding: '10px 0',
  border: `2px dashed ${TOKENS.borderStrong}`,
  borderRadius: TOKENS.radiusSm,
  color: TOKENS.text3,
  background: 'transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  fontSize: 12,
  fontWeight: 500,
  fontFamily: TOKENS.font,
}

/* ========================================================= Season Teams ==== */

const SeasonTeamsPanel = ({ teams, isLoading, actions, assignButton }) => {
  const hover = useHoverCard()
  const [query, setQuery] = useState('')
  const filtered = teams.filter((t) => (t.teamName || '').toLowerCase().includes(query.toLowerCase()))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: TOKENS.text3 }}>
              <Icon name="search" size={14} />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search teams…"
              style={{ height: 36, padding: '0 12px 0 32px', fontSize: 13, fontFamily: TOKENS.font, background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radiusSm, width: 260, outline: 'none' }}
            />
          </div>
          <div style={{ fontSize: 12, color: TOKENS.text3 }}>{filtered.length} of {teams.length} teams</div>
        </div>
        {assignButton}
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}><CircularProgress style={{ color: TOKENS.coral }} /></div>
      ) : teams.length === 0 ? (
        <Card><Empty icon="users" title="No teams assigned to this season" hint={assignButton ? 'Assign teams from your pool to start scheduling games.' : undefined} action={assignButton} /></Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {filtered.map((t) => (
            <div key={t.seasonTeamId} className={hover.card} style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
              <button type="button" onClick={() => actions.openTeamStats(t)} className={hover.nameHover} title="View team statistics" style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12, border: 'none', background: 'transparent', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'inherit' }}>
                <Avatar name={t.teamName} src={t.teamLogoUrl} color={avatarColor(t.teamName || '')} size={42} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.teamName}</div>
                </div>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {actions.editSeasonTeam && <IconBtn icon="edit" tone="coral" title="Rename" onClick={() => actions.editSeasonTeam(t)} />}
                {actions.removeTeam && <IconBtn icon="trash" tone="danger" title="Remove from season" onClick={() => actions.removeTeam(t)} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

/* ============================================================ Schedule ===== */
// Timeline of stages + chronological game lists. Scores aren't in the stages
// payload, so games show title/date/time/venue only.

const SchedulePanel = ({ season, stages, nav, actions }) => {
  const seasonStart = new Date(season.startDate).getTime()
  const seasonEnd = new Date(season.endDate).getTime()
  const span = seasonEnd - seasonStart || 1
  const now = Date.now()
  const todayPct = Math.max(0, Math.min(100, ((now - seasonStart) / span) * 100))

  const allGames = useMemo(() => {
    const out = []
    stages.forEach((st) =>
      (st.groups || []).forEach((g) =>
        (g.gamedays || []).forEach((gd) =>
          (gd.games || []).forEach((game) => out.push({ ...game, _stage: st, _group: g, _gd: gd })),
        ),
      ),
    )
    return out.filter((g) => g.startDateTime).sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime))
  }, [stages])

  const upcoming = allGames.filter((g) => new Date(g.startDateTime).getTime() >= now).slice(0, 4)
  const recent = allGames.filter((g) => new Date(g.startDateTime).getTime() < now).slice(-3).reverse()

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Card>
          <SectionTitle hint="Stages plotted on the season timeline.">Season Timeline</SectionTitle>
          <div style={{ position: 'relative', paddingTop: 32, paddingBottom: 8 }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, width: 1, background: TOKENS.coral, zIndex: 10, left: `${todayPct}%` }}>
              <div style={{ position: 'absolute', top: -4, transform: 'translateX(-50%)', padding: '2px 6px', background: TOKENS.coral, color: TOKENS.white, fontSize: 9, fontWeight: 700, borderRadius: 4, textTransform: 'uppercase' }}>Today</div>
            </div>
            <div style={{ position: 'relative', height: 48, background: TOKENS.surface2, borderRadius: TOKENS.radiusSm, overflow: 'hidden' }}>
              {stages.map((stage) => {
                if (!stage.startDate || !stage.endDate) return null
                const sStart = new Date(stage.startDate).getTime()
                const sEnd = new Date(stage.endDate).getTime()
                // Clamp the bar to the season window: a stage that starts before or
                // ends after the season must not overflow the track (e.g. a 1-month
                // stage inside a 3-day season would otherwise blow past the frame).
                const rawLeft = ((sStart - seasonStart) / span) * 100
                const rawRight = ((sEnd - seasonStart) / span) * 100
                const left = Math.max(0, Math.min(94, rawLeft))
                const width = Math.max(6, Math.min(100 - left, rawRight - left))
                const tone = periodTone(stage.startDate, stage.endDate)
                const bg = tone === 'live' ? TOKENS.liveBg : tone === 'upcoming' ? TOKENS.upcomingBg : TOKENS.pastBg
                const bc = tone === 'live' ? TOKENS.liveBorder : tone === 'upcoming' ? TOKENS.upcomingBorder : TOKENS.pastBorder
                const fc = tone === 'live' ? TOKENS.liveText : tone === 'upcoming' ? TOKENS.upcomingText : TOKENS.pastText
                return (
                  <button
                    key={stage.id}
                    type="button"
                    onClick={() => nav.openStage(stage)}
                    style={{ position: 'absolute', top: 4, bottom: 4, left: `${left}%`, width: `${width}%`, padding: '0 8px', borderRadius: 6, border: `1px solid ${bc}`, background: bg, color: fc, fontSize: 11, fontWeight: 500, textAlign: 'left', overflow: 'hidden', cursor: 'pointer' }}
                  >
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stage.name}</div>
                    <div style={{ fontSize: 10, opacity: 0.7, fontFamily: TOKENS.mono }}>{(stage.groups || []).length} {(stage.groups || []).length === 1 ? 'group' : 'groups'}</div>
                  </button>
                )
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 10, color: TOKENS.text3, fontFamily: TOKENS.mono }}>
              <span>{fmtDateShort(season.startDate)}</span>
              <span>{fmtDateShort(season.endDate)}</span>
            </div>
          </div>
        </Card>

        <Card>
          <SectionTitle hint="Most recent games across all stages.">Recent Games</SectionTitle>
          {recent.length === 0 ? (
            <div style={{ fontSize: 12, color: TOKENS.text3, padding: '8px 0' }}>No games played yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recent.map((g) => <GameRow key={g.id} game={g} tone="past" onClick={(e) => actions?.openGame?.(g.id, e)} />)}
            </div>
          )}
        </Card>
      </div>

      <Card>
        <SectionTitle>Up Next</SectionTitle>
        {upcoming.length === 0 ? (
          <div style={{ fontSize: 12, color: TOKENS.text3, padding: '8px 0' }}>Nothing scheduled.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {upcoming.map((g) => (
              <div key={g.id} onClick={(e) => actions?.openGame?.(g.id, e)} style={{ padding: 12, borderRadius: TOKENS.radiusSm, background: TOKENS.surface2, cursor: 'pointer' }}>
                <div style={{ fontSize: 10, fontFamily: TOKENS.mono, color: TOKENS.text3, marginBottom: 4 }}>{fmtDateShort(g.startDateTime)} · {fmtTime(g.startDateTime)}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: TOKENS.text }}>{matchupLabel(g)}</div>
                {g.place && (
                  <div style={{ fontSize: 10, color: TOKENS.text3, marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Icon name="mapPin" size={10} /> {g.place}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

const matchupLabel = (game) => {
  if (game.homeTeam && game.awayTeam) {
    const score = isFinal(game) ? ` ${game.homeTeam.score ?? 0}–${game.awayTeam.score ?? 0}` : ''
    return `${game.homeTeam.name} vs ${game.awayTeam.name}${score}`
  }
  return game.title || 'Game'
}

const GameRow = ({ game, tone, onClick }) => (
  <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: 12, borderRadius: TOKENS.radiusSm, border: `1px solid ${TOKENS.border}`, cursor: onClick ? 'pointer' : 'default' }}>
    <Badge tone={isFinal(game) ? 'past' : tone} dot>{isFinal(game) ? 'Final' : tone === 'past' ? 'Played' : 'Scheduled'}</Badge>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{matchupLabel(game)}</div>
      <div style={{ fontSize: 11, color: TOKENS.text3, fontFamily: TOKENS.mono, marginTop: 2, display: 'flex', gap: 8 }}>
        <span>{fmtDateShort(game.startDateTime)}</span><span>·</span><span>{fmtTime(game.startDateTime)}</span>
        {game.place && <><span>·</span><span>{game.place}</span></>}
      </div>
    </div>
  </div>
)

export default SeasonView
